import React, { useCallback, useEffect } from 'react'
import { Segmented } from 'antd'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'
import assignmentSelectors from '../../../../../../store/selectors/assignment.selectors'
import { isMba } from '../../../../../../utils/assignment.helper'
import { useFetchQuestionsQuery } from '../../../../../../store/apis/question.api'
import { fetchQuestionsSelectors } from '../../../../../../store/apis/utils/selectors/question.api.selectors'
import { useVersionId } from '../../../../../../utils/parseURL.helper'
import useQueryParam from '../../../../../../hooks/useQueryParam'
import useQueryParamSetter from '../../../../../../hooks/useQueryParamSetter'
import { TEST_SET_ONLY_PARAM } from '../../../../../../constants/query-params'
import { getQuestionsTrainingStats } from '../../../../../../utils/question.helper'

const TEST_SET_OPTIONS = [
    { label: 'All Submissions', value: 'all-submissions' },
    { label: 'Test Set', value: 'test-set' },
]

const TestSetToggle = () => {
    const assignmentType = useSelector(assignmentSelectors.type)
    const { token, layerId: questionId } = useParams()
    const versionId = useVersionId()
    const setQueryParam = useQueryParamSetter()
    const testSetOnlyParam = useQueryParam(TEST_SET_ONLY_PARAM)
    const testSetOnly = testSetOnlyParam === 'true'

    const { data: question } = useFetchQuestionsQuery(
        { token, versionId },
        {
            skip: questionId === 'all',
            selectFromResult: result => fetchQuestionsSelectors.byId({ id: Number(questionId) }, result),
        },
    )

    // Fetch all questions to check if all are trained
    const { data: allQuestions = [], isLoading: isLoadingAllQuestions } = useFetchQuestionsQuery(
        { token, versionId },
        {
            selectFromResult: result => ({ data: result.data || [] }),
        },
    )

    // Only show for fully trained models/questions, not for MBA
    const shouldShowTestSetToggle = useCallback(() => {
        if (isMba(assignmentType)) return false
        if (isLoadingAllQuestions) return false
        if (allQuestions.length === 0) return false

        if (questionId === 'all') {
            // For "all" view, check if all questions are trained
            const { allQuestionsTrained } = getQuestionsTrainingStats(allQuestions)
            return allQuestionsTrained
        } else {
            // For individual question view, check if question is trained
            return question?.lastTrained
        }
    }, [isMba(assignmentType), isLoadingAllQuestions, questionId, question, allQuestions, assignmentType])

    const handleTestSetChange = value =>
        setQueryParam({ [TEST_SET_ONLY_PARAM]: value === 'test-set' ? 'true' : 'false' })

    useEffect(() => {
        if (isLoadingAllQuestions) return

        if (shouldShowTestSetToggle() && !testSetOnlyParam) {
            setQueryParam({ [TEST_SET_ONLY_PARAM]: 'true' })
        }
    }, [isLoadingAllQuestions, testSetOnlyParam, setQueryParam, shouldShowTestSetToggle])

    if (!shouldShowTestSetToggle()) {
        return null
    }

    return (
        <Segmented
            options={TEST_SET_OPTIONS}
            value={testSetOnly ? 'test-set' : 'all-submissions'}
            onChange={handleTestSetChange}
        />
    )
}

export default TestSetToggle
